"use client"

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useAuth } from 'react-oidc-context';
import axios from 'axios';
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select'
import { useToast } from '../hooks/use-toast'
import { api } from '../lib/api-client'
import { getErrorMessage } from '../lib/api-utils'
import { Upload, Image as ImageIcon, AlertCircle, X, Layers } from 'lucide-react'
import LargeBatchUpload from './LargeBatchUpload'
import { sortMatchesByDate } from '../utils/playerSorting'

export default function UploadTab() {
  const [files, setFiles] = useState([])
  const [previews, setPreviews] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false)
  const [matches, setMatches] = useState([])
  const [selectedMatch, setSelectedMatch] = useState('')
  const [showNewMatchForm, setShowNewMatchForm] = useState(false)
  const [newMatchData, setNewMatchData] = useState({
    title: '',
    date: '',
    opponent: '',
    competition: '',
    venue: '',
    result: '',
    notes: ''
  })
  const [isCreatingMatch, setIsCreatingMatch] = useState(false)
  const [uploadMode, setUploadMode] = useState('standard') // 'standard' or 'large'

  const { toast } = useToast()
  const auth = useAuth();

  // State for async batch processing
  const [batchJobs, setBatchJobs] = useState(new Map()); // Track active batch jobs
  const [pollingIntervals, setPollingIntervals] = useState(new Map()); // Track polling intervals
  const fileInputRef = useRef(null);

  // Fetch matches from API
  const fetchMatches = useCallback(async () => {
    try {
      const response = await api.getMatches()
      
      if (response.success) {
        setMatches(response.data)
      } else {
        console.error('Failed to fetch matches:', response.message)
      }
    } catch (error) {
      console.error('Error fetching matches:', error)
    }
  }, [])

  useEffect(() => {
    fetchMatches()
  }, [fetchMatches])

  // Handle file selection
  const handleFilesSelect = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter(file => {
      if (!file.type.startsWith('image/')) {
        toast({
          variant: 'destructive',
          title: 'Invalid file type',
          description: `${file.name} is not an image file.`,
          duration: 3000
        })
        return false
      }
      return true
    })

    // Automatically switch to large batch mode if more than 10 files are selected
    if (validFiles.length > 10 && uploadMode === 'standard') {
      setUploadMode('large')
      toast({
        title: 'Switched to Large Batch Mode',
        description: `Automatically switched to large batch mode for ${validFiles.length} images.`,
        duration: 4000
      })
    }

    setFiles(validFiles)
    
    // Generate previews
    const newPreviews = []
    validFiles.forEach((file, index) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        newPreviews[index] = {
          preview: e.target.result,
          file: file
        }
        if (newPreviews.length === validFiles.length && newPreviews.every(p => p)) {
          setPreviews(newPreviews)
        }
      }
      reader.readAsDataURL(file)
    })
  }

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFiles = Array.from(e.dataTransfer.files)
    handleFilesSelect(droppedFiles)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  // Remove a file
  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index)
    const newPreviews = previews.filter((_, i) => i !== index)
    setFiles(newFiles)
    setPreviews(newPreviews)
  }

  // Create new match
  const handleCreateMatch = async () => {
    if (!newMatchData.title || !newMatchData.date) {
      toast({
        variant: 'destructive',
        title: 'Missing required fields',
        description: 'Please provide at least a title and date for the match.',
        duration: 5000
      })
      return
    }

    setIsCreatingMatch(true)
    try {
      const response = await api.createMatch(newMatchData)
      
      if (response.success) {
        toast({
          title: 'Match created successfully',
          description: `Match "${newMatchData.title}" has been created.`,
          duration: 3000
        })
        
        setMatches(prev => [...prev, response.data])
        setSelectedMatch(response.data.id)
        setShowNewMatchForm(false)
        setNewMatchData({
          title: '',
          date: '',
          opponent: '',
          competition: '',
          venue: '',
          result: '',
          notes: ''
        })
      } else {
        throw new Error(response.message || 'Failed to create match')
      }
    } catch (error) {
      console.error('Error creating match:', error)
      toast({
        variant: 'destructive',
        title: 'Failed to create match',
        description: getErrorMessage(error),
        duration: 5000
      })
    } finally {
      setIsCreatingMatch(false)
    }
  }

  // Start polling for batch job status
  const startBatchJobPolling = (jobId) => {
    // Function to poll job status
    const pollJobStatus = async () => {
      try {
        const statusResponse = await api.getBatchJobStatus(jobId);
        const jobData = statusResponse.data;
        if (jobData) {
          console.log(`📊 Job ${jobId.slice(0,8)}: ${jobData.status} (${jobData.progress?.processed || 0}/${jobData.progress?.total || 0})`);
        }
        
        if (statusResponse.success && statusResponse.data) {
          const jobData = statusResponse.data;
          
          // Update job state
          setBatchJobs(prev => {
            const updated = new Map(prev);
            const existing = updated.get(jobId) || {};
            updated.set(jobId, {
              ...existing,
              status: jobData.status,
              progress: jobData.progress,
              results: jobData.results,
              errors: jobData.errors,
              updatedAt: new Date()
            });
            return updated;
          });
          
          // Show progress toast periodically
          const lastNotified = localStorage.getItem(`batch-${jobId}-last-notification`);
          const shouldNotify = !lastNotified || (jobData.progress.percentage % 25 === 0 && 
            jobData.progress.percentage.toString() !== lastNotified);
          
          if (shouldNotify && jobData.progress.percentage > 0) {
            toast({
              title: `Batch Progress: ${jobData.progress.percentage}%`,
              description: `Processed ${jobData.progress.processed}/${jobData.progress.total} images`,
              duration: 3000,
            });
            localStorage.setItem(`batch-${jobId}-last-notification`, jobData.progress.percentage.toString());
          }
          
          // Handle completion
          if (jobData.status === 'completed' || jobData.status === 'failed') {
            console.log(`🎉 Job ${jobId.slice(0,8)} finished:`, jobData.status);
            clearInterval(pollInterval);
            
            setPollingIntervals(prev => {
              const updated = new Map(prev);
              updated.delete(jobId);
              return updated;
            });
            
            const successCount = jobData.progress.processed;
            const failedCount = jobData.progress.failed;
            
            if (jobData.status === 'completed') {
              toast({
                title: 'Batch Processing Complete!',
                description: `Successfully processed ${successCount} images${failedCount > 0 ? `, ${failedCount} failed` : ''}. Check the Library tab to view results.`,
                duration: 10000,
              });
            } else {
              toast({
                variant: 'destructive',
                title: 'Batch Processing Failed', 
                description: `Job failed after processing ${successCount} images. Check the console for details.`,
                duration: 10000,
              });
            }
            
            // Clean up localStorage
            localStorage.removeItem(`batch-${jobId}-last-notification`);
          }
        }
      } catch (error) {
        console.error(`Error polling job ${jobId}:`, error);
      }
    };
    
    // Start immediate polling and then set interval
    pollJobStatus();
    const pollInterval = setInterval(pollJobStatus, 2000); // Poll every 2 seconds
    setPollingIntervals(prev => new Map(prev.set(jobId, pollInterval)));
  }

  // Cleanup polling intervals on component unmount
  useEffect(() => {
    return () => {
      pollingIntervals.forEach((interval) => {
        clearInterval(interval);
      });
    };
  }, [pollingIntervals]);

  // Handle file upload with async batch processing
  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }
    if (!selectedMatch) {
      setError('Please select a match before uploading.');
      toast({
        title: "Match Required",
        description: "Please select or create a match before uploading images.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Step 1: Get presigned URLs from the backend
      const filesToGetUrlsFor = Array.from(files).map(file => ({ filename: file.name, filetype: file.type }));
      
      const presignedUrlResponse = await api.generateUploadUrls({ files: filesToGetUrlsFor });
      console.log('✅ Got presigned URLs for', presignedUrlResponse.data?.length, 'files');

      if (!presignedUrlResponse.success) {
        throw new Error(presignedUrlResponse.error || 'Could not get upload URLs from server.');
      }
      const presignedUrls = presignedUrlResponse.data;

      // Step 2: Upload files directly to S3
      
      const uploadPromises = presignedUrls.map((item, index) => {
        const file = files[index];
        return axios.put(item.url, file, {
          headers: { 'Content-Type': file.type }
        });
      });
      
      await Promise.all(uploadPromises);
      console.log('✅ S3 uploads completed');

      // Step 3: Notify backend to start analysis
      const s3Keys = presignedUrls.map(item => item.s3Key);
      const metadata = { 
        matchId: selectedMatch !== 'new' ? selectedMatch : null,
        uploader: auth.isAuthenticated && auth.user && auth.user.profile ? auth.user.profile.email : 'system',
        uploadedBy: auth.isAuthenticated && auth.user && auth.user.profile ? auth.user.profile.email : 'system'
      };
      
      const batchJobResponse = await api.uploadBatch({ s3Keys, metadata });
      console.log('✅ Batch job created:', batchJobResponse.data?.jobId);

      if (batchJobResponse.success && batchJobResponse.data.jobId) {
        const { jobId } = batchJobResponse.data;
        const newJob = {
          jobId,
          totalImages: files.length,
          status: 'pending',
          progress: { processed: 0, failed: 0, percentage: 0 },
          startTime: new Date(),
          previews: previews.map(p => p.preview)
        };
        setBatchJobs(prev => new Map(prev.set(jobId, newJob)));
        startBatchJobPolling(jobId);
        toast({ title: 'Batch Upload Started', description: `Processing ${files.length} images.` });
        setFiles([]);
        setPreviews([]);
      } else {
        throw new Error(batchJobResponse.message || 'Failed to start analysis job.');
      }

    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      toast({ variant: 'destructive', title: 'Upload Failed', description: errorMessage });
    } finally {
      setIsUploading(false);
    }
  };

  // Memoized sorted matches to prevent sorting on every render
  const sortedMatches = useMemo(() => {
    return sortMatchesByDate(matches);
  }, [matches]);

  return (
    <div className="space-y-6">
      {/* Upload Mode Toggle */}
      <div className="flex items-center justify-center gap-4 p-4 bg-gray-50 rounded-lg">
        <span className="text-sm font-medium">Upload Mode:</span>
        <div className="flex bg-white rounded-lg p-1 border">
          <button
            onClick={() => setUploadMode('standard')}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              uploadMode === 'standard' 
                ? 'bg-blue-600 text-white' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
            title="Best for small uploads. Images are processed immediately with real-time progress."
          >
            <Upload className="h-4 w-4 inline mr-1" />
            Standard (1-10 images)
          </button>
          <button
            onClick={() => setUploadMode('large')}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              uploadMode === 'large' 
                ? 'bg-blue-600 text-white' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
            title="For large uploads. Files are uploaded in chunks and processed in the background."
          >
            <Layers className="h-4 w-4 inline mr-1" />
            Large Batch (11+ images)
          </button>
        </div>
      </div>

      {uploadMode === 'large' ? (
        <LargeBatchUpload 
          matches={matches}
          onComplete={() => {
            toast({
              title: "Batch Complete",
              description: "Large batch upload and processing completed successfully.",
            })
          }}
        />
      ) : (
        <>
          {/* Standard Upload UI */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              Drop images here or click to select
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Standard upload for 1-10 images with immediate processing.
            </p>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleFilesSelect(Array.from(e.target.files))}
          className="hidden"
          id="file-upload"
          ref={fileInputRef}
        />
        <Button 
          variant="outline" 
          className="cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          type="button"
        >
          Select Images
        </Button>
      </div>

      {/* Match Selection */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="match-select">Select Match *</Label>
          <div className="flex gap-2">
            <Select value={selectedMatch} onValueChange={setSelectedMatch}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Choose a match for these images" />
              </SelectTrigger>
              <SelectContent>
                {sortedMatches.map(match => (
                    <SelectItem key={match.id} value={match.id}>
                      {match.title} - {match.date}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={() => setShowNewMatchForm(!showNewMatchForm)}
            >
              New Match
            </Button>
          </div>
        </div>

        {/* New Match Form */}
        {showNewMatchForm && (
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="match-title">Match Title *</Label>
                  <Input
                    id="match-title"
                    value={newMatchData.title}
                    onChange={(e) => setNewMatchData({...newMatchData, title: e.target.value})}
                    placeholder="e.g., Shamrock Rovers vs Bohemians"
                  />
                </div>
                <div>
                  <Label htmlFor="match-date">Date *</Label>
                  <Input
                    id="match-date"
                    type="date"
                    value={newMatchData.date}
                    onChange={(e) => setNewMatchData({...newMatchData, date: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="opponent">Opponent</Label>
                  <Input
                    id="opponent"
                    value={newMatchData.opponent}
                    onChange={(e) => setNewMatchData({...newMatchData, opponent: e.target.value})}
                    placeholder="Opposition team"
                  />
                </div>
                <div>
                  <Label htmlFor="competition">Competition</Label>
                  <Input
                    id="competition"
                    value={newMatchData.competition}
                    onChange={(e) => setNewMatchData({...newMatchData, competition: e.target.value})}
                    placeholder="e.g., League of Ireland"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="venue">Venue</Label>
                <Input
                  id="venue"
                  value={newMatchData.venue}
                  onChange={(e) => setNewMatchData({...newMatchData, venue: e.target.value})}
                  placeholder="e.g., Tallaght Stadium"
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newMatchData.notes}
                  onChange={(e) => setNewMatchData({...newMatchData, notes: e.target.value})}
                  placeholder="Additional notes about the match"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateMatch} disabled={isCreatingMatch}>
                  {isCreatingMatch ? 'Creating...' : 'Create Match'}
                </Button>
                <Button variant="outline" onClick={() => setShowNewMatchForm(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Selected Files Preview */}
      {files.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-4">Selected Images ({files.length})</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {files.map((file, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  {previews[index] ? (
                    <img
                      src={previews[index].preview}
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
                <p className="text-xs text-gray-500 mt-1 truncate">{file.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Upload Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleUpload}
          disabled={isUploading || files.length === 0 || !selectedMatch}
          className="min-w-[120px]"
        >
          {isUploading ? 'Starting...' : `Upload ${files.length} Image${files.length !== 1 ? 's' : ''}`}
        </Button>
      </div>

          {/* Active Batch Jobs Status */}
          {batchJobs.size > 0 && (
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Active Batch Jobs</h3>
              {Array.from(batchJobs.values()).map(job => (
                <Card key={job.jobId}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">Job {job.jobId.slice(0, 8)}...</p>
                        <p className="text-sm text-gray-500">{job.totalImages} images • {job.status}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{job.progress.percentage || 0}%</p>
                        <p className="text-sm text-gray-500">
                          {job.progress.processed || 0}/{job.totalImages}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${job.progress.percentage || 0}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}